import {
  buildCompanionDecision,
  rankCompanionDecisionPlaces,
  type CompanionDecision,
} from "./decision";
import {
  buildCompanionTrustState,
  type CompanionCrowdReport,
  type CompanionGroundTruthAgreement,
  type CompanionTrustState,
} from "./trust";
import { scoreOutOf100Label } from "@/lib/conditions/score-display";
import type { CompanionDayIntentDaySummary } from "./day-intents";
import {
  buildBaseChipLabel,
  buildBaseGroups,
  buildViewedBaseSummary,
  findAssignablePlaceIds,
  pickDecisionPlaces,
  type CompanionBaseGroup,
  type CompanionBaseRecord,
} from "./bases";
import type { CompanionAvailabilityWindow } from "./availability-fit";
import type { CompanionStoredForecastDay } from "./week-rail";
import type { TodayRegionData } from "@/lib/today/types";
import type { CompanionRecordSummary } from "./record-summary";
import type { CompanionBriefingPreferences } from "./briefing-preferences";
import type { CompanionSquadSummary } from "./squads-server";
import type { GroundTruthSport } from "@/lib/guardian/ground-truth-reports";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";
import type { MtbGroundTruthPrefill } from "@/lib/conditions/mtb-ground-truth";
import type { CompanionRecommendationData } from "./recommendation-data";
import { bestWeekCell } from "@/lib/today/week";
import { conditionsSpotHref } from "@/lib/seo/conditions-links";
import { buildAreaVerdict, type AreaVerdictKind } from "@/lib/areas/verdict";
import type { AreaBoard } from "@/lib/areas/row-model";
import { localComparisonHref, localPublicLink, localSportLabel } from "./local-comparison-links";

export type CompanionSport = "ski" | "surf" | "festival" | "mixed";
export type CompanionScoreKind = "generic-fallback" | "sport-specific";

export type CompanionSavedPlace = {
  id: string;
  slug: string | null;
  name: string;
  region: string | null;
  sport: CompanionSport;
  score: number | null;
  scoreKind: CompanionScoreKind;
  verdict: string | null;
  bestWindow: string | null;
  remainingBestWindow?: string | null;
  updatedAt: string | null;
  safetyNotes?: string[];
  safetyState?: ConditionSafetyState;
  sourceCount?: number;
  crowdReports?: CompanionCrowdReport[];
  groundTruth?: CompanionGroundTruthAgreement | null;
  briefingEnabled?: boolean;
  callSports?: string[];
  baseId?: string | null;
  createdAt?: string | null;
  isFestival?: boolean;
  latitude?: number | null;
  longitude?: number | null;
  preferredSport?: string | null;
  locationType?: string | null;
  groundTruthSport?: GroundTruthSport | null;
  groundTruthPrefill?: MtbGroundTruthPrefill | null;
  storedForecast?: CompanionStoredForecastDay[] | null;
  href: string;
};

export type CompanionNearbyCandidate = {
  id: string;
  slug: string;
  name: string;
  region: string | null;
  locationType: string | null;
  sportKey: string;
  sportLabel: string;
  score: number;
  href: string;
  distanceKm: number;
  driveMinutes: number;
  updatedAt: string | null;
  safetyNotes?: string[];
  safetyState?: ConditionSafetyState;
};

export type CompanionBase = CompanionBaseRecord & {
  anchorName: string | null;
  anchorRegion: string | null;
  latitude: number | null;
  longitude: number | null;
};

export type CompanionDownDayModule = {
  regionName: string;
  regionSlug: string;
  regionKind: TodayRegionData["region"]["kind"];
  href: string;
  heading: string;
  fallbackLinkLabel: string;
  bestDay: {
    weekday: string;
    dateISO: string;
    sportKey: string;
    locationSlug: string;
    score: number;
    /** The best day's spot, opened on its sport lens. */
    href: string;
  } | null;
  guides: {
    slug: string;
    name: string;
    href: string;
  }[];
  festival: {
    slug: string;
    name: string;
    datesLabel: string;
    score: number | null;
    state: string | null;
    href: string;
  } | null;
};

export type CompanionCallSummary = {
  id: string;
  title: string;
  placeName: string | null;
  verdict: string | null;
  startsAt: string | null;
  unread: boolean;
  href: string;
} | null;

export type CompanionBriefingSummary = {
  id: string;
  title: string;
  placeName: string | null;
  publishedAt: string | null;
  href: string;
} | null;

export type CompanionCrewSummary = {
  id: string;
  name: string;
  unread: boolean;
  href: string;
}[];

export type CompanionTrophySummary = Readonly<{
  trophyId: string;
  awardedAt: string | null;
}>;

export type CompanionProofSummary = Readonly<
  Omit<CompanionRecordSummary, "sessionsBySport"> & {
    sessionsBySport: Readonly<CompanionRecordSummary["sessionsBySport"]>;
    trophies: number;
    latestTrophies: readonly CompanionTrophySummary[];
    latestLabel: string | null;
    wrappedCards: number;
    groundTruthReportCount: number | null;
    href: string;
  }
>;

export const EMPTY_COMPANION_PROOF: CompanionProofSummary = Object.freeze({
  trackedDays: 0,
  thisMonthDays: 0,
  weeklyStreak: 0,
  bestWeeklyStreak: 0,
  sportsAllTime: 0,
  sessionsBySport: Object.freeze({ ski: 0, mtb: 0, coastal: 0, surf: 0 }),
  trophies: 0,
  latestTrophies: Object.freeze([]),
  latestLabel: null,
  wrappedCards: 0,
  groundTruthReportCount: null,
  href: "/companion/passport",
});

export type CompanionFirstRunChecklistStep = {
  id: "set-base" | "follow-sports" | "choose-briefing";
  label: string;
  done: boolean;
  href: string;
  ctaLabel: string;
  statusLabel?: string;
};

export type CompanionHomeData = {
  user: { id: string; displayName: string | null } | null;
  savedPlaces: CompanionSavedPlace[];
  availabilityWindows: CompanionAvailabilityWindow[];
  availabilityManagementWindows: CompanionAvailabilityWindow[];
  nearbyCandidates: CompanionNearbyCandidate[];
  nearbyTravelTimeCapMinutes: number | null;
  bases: CompanionBase[];
  activeBaseId?: string | null;
  briefingPreferences: CompanionBriefingPreferences;
  downDay: CompanionDownDayModule | null;
  latestCall: CompanionCallSummary;
  latestBriefing: CompanionBriefingSummary;
  crews: CompanionCrewSummary;
  squads: CompanionSquadSummary[];
  proof: CompanionProofSummary;
  groundTruthPromptPlace: CompanionSavedPlace | null;
  dayIntents?: CompanionDayIntentDaySummary[];
};

export type CompanionTodayCoreData = Omit<
  CompanionHomeData,
  "crews" | "squads" | "proof" | "dayIntents" | "groundTruthPromptPlace"
> & {
  recommendationData: CompanionRecommendationData | null;
  recommendationUnavailable: boolean;
  todayContext?: import("./today-context").TodayContext;
};

export type CompanionPeopleSource =
  | "crews"
  | "squads"
  | "roll-call"
  | "day-intents";

export type CompanionPeoplePulseData = {
  status: "ready" | "empty" | "partial" | "error";
  crews: CompanionCrewSummary;
  squads: CompanionSquadSummary[];
  dayIntents: CompanionDayIntentDaySummary[];
  unavailableSources: CompanionPeopleSource[];
};

export function companionHomeDataFromCore(
  core: CompanionTodayCoreData,
): CompanionHomeData {
  return {
    ...core,
    crews: [],
    squads: [],
    proof: EMPTY_COMPANION_PROOF,
    groundTruthPromptPlace: null,
    dayIntents: [],
  };
}

export type CompanionAttentionItem = {
  id: string;
  kind: "setup" | "place" | "call" | "briefing" | "crew" | "record";
  title: string;
  body: string;
  href: string;
  priority: number;
  score: number | null;
  meta: string | null;
};

export type CompanionHomeModel = {
  status: "anonymous" | "empty" | "ready";
  decision: CompanionDecision;
  decisionPlace: CompanionSavedPlace | null;
  trust: CompanionTrustState;
  today: CompanionAttentionItem[];
  allSavedPlaces: CompanionSavedPlace[];
  activeMountainOrCoast: CompanionSavedPlace[];
  mountainOrCoast: CompanionSavedPlace[];
  visiblePlace: CompanionSavedPlace | null;
  visiblePlaceTrust: CompanionTrustState | null;
  bases: CompanionBase[];
  activeBase: CompanionBase | null;
  viewedBase: CompanionBase | null;
  baseChipLabel: string;
  baseOverrideMessage: string | null;
  noActiveBasePrompt: string | null;
  needsBaseMigration: boolean;
  assignBasePrompt:
    | {
        baseId: string;
        baseName: string;
        count: number;
        placeIds: string[];
      }
    | null;
  placeGroups: CompanionBaseGroup[];
  checklist: CompanionFirstRunChecklistStep[];
  readyConfirmation: {
    title: string;
    body: string;
    href: string;
  } | null;
  crew: CompanionCrewSummary;
  squads: CompanionSquadSummary[];
  record: CompanionProofSummary;
  groundTruthPromptPlace: CompanionSavedPlace | null;
  dayIntents: CompanionDayIntentDaySummary[];
  primaryCta: { label: string; href: string };
};

export type CompanionHomeModelOptions = {
  now?: Date;
  viewedBaseId?: string | null;
};

export function normaliseCompanionSport(value: string | null | undefined): CompanionSport {
  const key = value?.toLowerCase().trim();
  if (!key) return "mixed";
  if (["ski", "snow", "snowboard", "piste", "touring"].includes(key)) return "ski";
  if (["surf", "coastal", "sailing", "wind", "swim"].includes(key)) return "surf";
  if (["festival", "festivals"].includes(key)) return "festival";
  return "mixed";
}

const GROUND_TRUTH_SPORTS = new Set<GroundTruthSport>([
  "ski",
  "piste",
  "off-piste",
  "touring",
  "mtb",
  "surf",
  "wind",
  "foil",
  "downwind-foil",
  "wing-foil",
  "bodyboarding",
  "diving",
  "swim",
  "sup",
  "sup-touring",
  "sup-surf",
  "sup-downwind",
  "sailing",
]);

export function normaliseGroundTruthSport(
  value: string | null | undefined,
): GroundTruthSport | null {
  const key = value?.toLowerCase().trim();
  if (!key) return null;
  if (key === "snowboard" || key === "snow") return "ski";
  if (key === "ski-touring" || key === "ski_touring") return "touring";
  if (key === "offpiste" || key === "off-piste") return "off-piste";
  if (key === "sailing-club" || key === "sailing_hp" || key === "sailing-hp") {
    return "sailing";
  }
  if (key === "coastal") return "surf";
  return GROUND_TRUTH_SPORTS.has(key as GroundTruthSport)
    ? (key as GroundTruthSport)
    : null;
}

export function resolveNativeWidgetHomeResortSlug(
  model: Pick<CompanionHomeModel, "activeBase" | "allSavedPlaces">,
): string | null {
  const activeAnchorSlug = model.activeBase?.anchorSlug?.trim() || null;
  if (activeAnchorSlug && isSkiLocationType(model.activeBase?.anchorType)) {
    return activeAnchorSlug;
  }

  const firstSkiFavourite = model.allSavedPlaces.find((place) => {
    if (place.isFestival) return false;
    if (!place.slug) return false;
    return isSkiLocationType(place.locationType) || place.sport === "ski";
  });

  return firstSkiFavourite?.slug?.trim() || null;
}

/** Where the down-day block sends people when it has no best day to show. */
export const COMPANION_DOWN_DAY_FALLBACK_HREF = "/companion/explore/events";

export function buildCompanionDownDayModule(
  data: TodayRegionData,
): CompanionDownDayModule {
  // The highest scoring unflagged day, not merely the first day with any best.
  const bestDayCell = bestWeekCell(data.week);
  const bestDay = bestDayCell?.best ?? null;

  return {
    regionName: data.region.name,
    regionSlug: data.region.slug,
    regionKind: data.region.kind,
    href: COMPANION_DOWN_DAY_FALLBACK_HREF,
    heading: companionDownDayHeading(data.region.kind),
    fallbackLinkLabel: `See what else is on in ${data.region.name}`,
    bestDay:
      bestDay && bestDayCell
        ? {
            weekday: bestDayCell.weekday,
            dateISO: bestDayCell.dateISO,
            sportKey: bestDay.sportKey,
            locationSlug: bestDay.locationSlug,
            score: bestDay.score,
            href: conditionsSpotHref(bestDay.locationSlug, bestDay.sportKey),
          }
        : null,
    guides: data.guides.slice(0, 2).map((guide) => ({
      slug: guide.slug,
      name: guide.name,
      href: `/guides/${guide.slug}`,
    })),
    festival: data.festival
      ? {
          slug: data.festival.slug,
          name: data.festival.name,
          datesLabel: data.festival.datesLabel,
          score: data.festival.score,
          state: data.festival.state,
          href: `/festivals/${data.festival.slug}`,
        }
      : null,
  };
}

export function sortPlacesByAttention(
  places: CompanionSavedPlace[],
  options: CompanionHomeModelOptions = {},
): CompanionSavedPlace[] {
  const now = options.now ?? inferCompanionNow({ savedPlaces: places });
  return rankCompanionDecisionPlaces(places, now);
}

export function buildTodayItems(
  input: CompanionHomeData,
  options: CompanionHomeModelOptions = {},
): CompanionAttentionItem[] {
  if (!input.user) {
    return [
      {
        id: "public-conditions",
        kind: "setup",
        title: "See what is good now",
        body: "Start with live conditions, then build your Companion around the places and seasons you care about.",
        href: "/conditions",
        priority: 10,
        score: null,
        meta: "Public preview",
      },
    ];
  }

  const now = options.now ?? inferCompanionNow(input);
  const places = sortPlacesByAttention(input.savedPlaces, { now });
  const items: CompanionAttentionItem[] = [];
  const bestPlace = places.find((place) => place.score !== null) ?? places[0] ?? null;

  if (bestPlace) {
    items.push({
      id: `place-${bestPlace.id}`,
      kind: "place",
      title: bestPlace.name,
      body: placeBody(bestPlace),
      href: bestPlace.href,
      priority: 100,
      score: bestPlace.score,
      meta: bestPlace.bestWindow ?? bestPlace.region,
    });
  }

  if (input.latestCall) {
    items.push({
      id: `call-${input.latestCall.id}`,
      kind: "call",
      title: input.latestCall.title,
      body: input.latestCall.verdict ?? callBody(input.latestCall.placeName),
      href: input.latestCall.href,
      priority: 90,
      score: null,
      meta: input.latestCall.startsAt,
    });
  }

  if (input.latestBriefing) {
    items.push({
      id: `briefing-${input.latestBriefing.id}`,
      kind: "briefing",
      title: input.latestBriefing.title,
      body: input.latestBriefing.placeName
        ? `Fresh briefing for ${input.latestBriefing.placeName}.`
        : "Fresh briefing from the places you follow.",
      href: input.latestBriefing.href,
      priority: 80,
      score: null,
      meta: input.latestBriefing.publishedAt,
    });
  }

  const unreadCrew = input.crews.find((crew) => crew.unread);
  if (unreadCrew) {
    items.push({
      id: `crew-${unreadCrew.id}`,
      kind: "crew",
      title: unreadCrew.name,
      body: "Your crew has new activity.",
      href: unreadCrew.href,
      priority: 70,
      score: null,
      meta: "Crew",
    });
  }

  if (input.proof.latestLabel || input.proof.trackedDays > 0 || input.proof.trophies > 0) {
    items.push({
      id: "record-proof",
      kind: "record",
      title: "Your season record",
      body: proofBody(input.proof),
      href: input.proof.href,
      priority: 50,
      score: null,
      meta: `${input.proof.trackedDays} tracked ${input.proof.trackedDays === 1 ? "day" : "days"}`,
    });
  }

  if (items.length === 0) {
    return [
      {
        id: "save-place",
        kind: "setup",
        title: "Save a place",
        body: "Follow the mountain or coast places you want PeakWave to watch.",
        href: "/conditions",
        priority: 20,
        score: null,
        meta: "Setup",
      },
      {
        id: "track-session",
        kind: "setup",
        title: "Track a session",
        body: "Log the next day out and start building your private season record.",
        href: "/companion/track",
        priority: 10,
        score: null,
        meta: "Setup",
      },
    ];
  }

  return items.sort((a, b) => b.priority - a.priority);
}

export function buildCompanionHomeModel(
  input: CompanionHomeData,
  options: CompanionHomeModelOptions = {},
): CompanionHomeModel {
  const now = options.now ?? inferCompanionNow(input);
  const activeBase =
    input.bases.find((base) => base.id === (input.activeBaseId ?? null)) ?? null;
  const viewedBase =
    input.bases.find((base) => base.id === (options.viewedBaseId ?? "")) ?? null;
  const viewedBaseOrActive = viewedBase ?? activeBase;
  const decisionInputPlaces = pickDecisionPlaces(input.savedPlaces, {
    activeBaseId: activeBase?.id ?? null,
  });
  const decision = buildCompanionDecision({
    savedPlaces: decisionInputPlaces,
    latestCall: input.latestCall,
    latestBriefing: input.latestBriefing,
    now,
  });
  const decisionPlaces = leadDecisionPlace(
    sortPlacesByAttention(decisionInputPlaces, { now }),
    decision.placeId,
  );
  const scopedViewedPlaces = viewedBaseOrActive
    ? sortPlacesByAttention(
        input.savedPlaces.filter(
          (place) => !place.isFestival && place.baseId === viewedBaseOrActive.id,
        ),
        { now },
      )
    : sortPlacesByAttention(
        input.savedPlaces.filter((place) => !place.isFestival),
        { now },
      );
  const placeGroups = buildBaseGroups({
    now,
    bases: input.bases,
    places: input.savedPlaces.map((place) => ({
      id: place.id,
      slug: place.slug,
      name: place.name,
      href: place.href,
      createdAt: place.createdAt ?? null,
      baseId: place.baseId ?? null,
      isFestival: place.isFestival ?? false,
    })),
  });
  const decisionPlace = decision.placeId
    ? decisionPlaces.find((place) => place.id === decision.placeId) ?? null
    : decisionPlaces[0] ?? null;
  const trust = buildDecisionTrust(decisionPlace, decision.score, now);
  const visiblePlace = viewedBase
    ? scopedViewedPlaces[0] ?? null
    : decisionPlace ?? scopedViewedPlaces[0] ?? null;
  const visiblePlaceTrust = visiblePlace
    ? buildDecisionTrust(visiblePlace, visiblePlace.score, now)
    : null;
  const today = buildTodayItems({ ...input, savedPlaces: decisionPlaces }, { now });
  const status = input.user ? (hasReadyData(input) ? "ready" : "empty") : "anonymous";
  const assignPlaceIds = findAssignablePlaceIds(input.savedPlaces, viewedBaseOrActive);

  return {
    status,
    decision,
    decisionPlace,
    trust,
    today,
    allSavedPlaces: input.savedPlaces,
    activeMountainOrCoast: decisionPlaces,
    mountainOrCoast: scopedViewedPlaces,
    visiblePlace,
    visiblePlaceTrust,
    bases: input.bases,
    activeBase,
    viewedBase,
    baseChipLabel: buildBaseChipLabel(viewedBaseOrActive, { now }),
    baseOverrideMessage:
      viewedBase && viewedBase.id !== activeBase?.id
        ? buildViewedBaseSummary(viewedBase, activeBase)
        : null,
    noActiveBasePrompt:
      activeBase || input.bases.length === 0
        ? null
        : "No active base. Set a home base or add a trip.",
    needsBaseMigration:
      input.savedPlaces.some((place) => !place.isFestival) && input.bases.length === 0,
    assignBasePrompt:
      viewedBaseOrActive && assignPlaceIds.length > 0
        ? {
            baseId: viewedBaseOrActive.id,
            baseName: viewedBaseOrActive.name,
            count: assignPlaceIds.length,
            placeIds: assignPlaceIds,
          }
        : null,
    placeGroups:
      viewedBase && viewedBase.id !== activeBase?.id
        ? leadViewedGroup(placeGroups, viewedBase.id)
        : placeGroups,
    checklist: buildFirstRunChecklist(input, decisionPlaces),
    readyConfirmation: buildReadyConfirmation(decisionPlaces, activeBase),
    crew: input.crews,
    squads: input.squads,
    record: input.proof,
    groundTruthPromptPlace: input.groundTruthPromptPlace ?? null,
    dayIntents: input.dayIntents ?? [],
    primaryCta: primaryCta(status, decisionPlaces, today),
  };
}

function buildReadyConfirmation(
  places: CompanionSavedPlace[],
  activeBase: CompanionBase | null,
): CompanionHomeModel["readyConfirmation"] {
  const place = places.find((item) => item.briefingEnabled);
  if (!place) return null;
  const watchedName = activeBase?.name.trim() || place.name;
  return {
    title: "You're set.",
    body: `We'll watch ${watchedName} and brief you when it matters. Email and push stay off unless you choose them.`,
    href: place.href,
  };
}

function buildFirstRunChecklist(
  input: CompanionHomeData,
  _places: CompanionSavedPlace[],
): CompanionFirstRunChecklistStep[] {
  const hasFollowedSport = input.savedPlaces.some(
    (place) => (place.callSports ?? []).filter((sport) => sport.trim().length > 0).length > 0,
  );
  const festivalOnly =
    input.savedPlaces.length > 0 && input.savedPlaces.every(isFestivalSavedPlace);
  const followSportsDone = hasFollowedSport || festivalOnly;
  const followSportsHref = "/companion/calls";

  return [
    {
      id: "set-base",
      label: "Set your base",
      done: input.bases.length > 0,
      href: "/companion?base-sheet=open",
      ctaLabel: "Set base",
    },
    {
      id: "follow-sports",
      label: "Follow your sports",
      done: followSportsDone,
      href: followSportsHref,
      ctaLabel: "Follow sports",
      ...(festivalOnly ? { statusLabel: "Not needed" } : {}),
    },
    {
      id: "choose-briefing",
      label: "Choose your briefing",
      done: input.briefingPreferences.hasPreference,
      href: "/companion#briefing-settings",
      ctaLabel: "In-app only is fine",
    },
  ];
}

function isFestivalSavedPlace(place: Pick<CompanionSavedPlace, "isFestival" | "sport">): boolean {
  return Boolean(place.isFestival) || place.sport === "festival";
}

function buildDecisionTrust(
  place: CompanionSavedPlace | null,
  score: number | null,
  now: Date,
): CompanionTrustState {
  return buildCompanionTrustState({
    score,
    scoreKind: place?.scoreKind ?? "generic-fallback",
    updatedAt: place?.updatedAt ?? null,
    sourceCount: place?.sourceCount ?? (place?.score !== null && place?.score !== undefined ? 1 : 0),
    crowdReports: place?.crowdReports ?? [],
    groundTruth: place?.groundTruth ?? null,
    now,
  });
}

function primaryCta(
  status: CompanionHomeModel["status"],
  places: CompanionSavedPlace[],
  today: CompanionAttentionItem[],
): { label: string; href: string } {
  if (status === "anonymous") {
    return { label: "Start your Companion", href: "/signup" };
  }
  if (status === "empty") {
    return { label: "Save a place", href: "/conditions" };
  }
  const bestPlace = places[0];
  if (bestPlace) {
    return { label: `Open ${bestPlace.name}`, href: bestPlace.href };
  }
  const first = today[0];
  return { label: first?.title ?? "Open Companion", href: first?.href ?? "/companion" };
}

function hasReadyData(input: CompanionHomeData): boolean {
  return (
    input.bases.length > 0 ||
    input.savedPlaces.length > 0 ||
    input.latestCall !== null ||
    input.latestBriefing !== null ||
    input.crews.length > 0 ||
    input.squads.length > 0 ||
    input.proof.latestLabel !== null ||
    input.proof.trackedDays > 0 ||
    input.proof.trophies > 0
  );
}

function placeBody(place: CompanionSavedPlace): string {
  if (place.verdict && place.bestWindow) {
    return `${place.verdict}. Best window: ${place.bestWindow}.`;
  }
  if (place.verdict) return place.verdict;
  if (place.score !== null) return `Current score is ${scoreOutOf100Label(place.score)}.`;
  return "No fresh score yet. Open the place for the latest context PeakWave has.";
}

function proofBody(proof: CompanionProofSummary): string {
  if (proof.latestLabel) return proof.latestLabel;
  if (proof.trophies > 0 && proof.trackedDays > 0) {
    return "Your tracked days and trophies are ready when you want proof.";
  }
  if (proof.trophies > 0) {
    return "Your trophies are ready when you want proof.";
  }
  return "Your tracked days are ready when you want proof.";
}

function callBody(placeName: string | null): string {
  return placeName
    ? `${placeName} has a rare-condition Call ready.`
    : "A rare-condition Call is ready.";
}

function companionDownDayHeading(
  kind: CompanionDownDayModule["regionKind"],
): string {
  if (kind === "mountain") return "Not a mountain day? Here is what else is on.";
  if (kind === "coast") return "Not a water day? Here is what else is on.";
  return "Not the day for it? Here is what else is on.";
}

function isSkiLocationType(value: string | null | undefined): boolean {
  return value?.toLowerCase().trim() === "ski";
}

function leadDecisionPlace(
  places: CompanionSavedPlace[],
  placeId: string | null,
): CompanionSavedPlace[] {
  if (!placeId) return places;
  const place = places.find((item) => item.id === placeId);
  if (!place) return places;
  return [place, ...places.filter((item) => item.id !== placeId)];
}

function inferCompanionNow(
  input: Pick<CompanionHomeData, "savedPlaces"> &
    Partial<Pick<CompanionHomeData, "latestCall" | "latestBriefing" | "bases">>,
): Date {
  const timestamps = [
    ...input.savedPlaces.map((place) => place.updatedAt),
    ...((input.bases ?? []).map((base) => base.createdAt)),
    input.latestBriefing?.publishedAt ?? null,
    input.latestCall?.startsAt ?? null,
  ]
    .map((value) => (value ? Date.parse(value) : NaN))
    .filter(Number.isFinite);

  if (timestamps.length === 0) return new Date(0);
  return new Date(Math.max(...timestamps));
}

function leadViewedGroup(
  groups: CompanionBaseGroup[],
  baseId: string,
): CompanionBaseGroup[] {
  const group = groups.find((item) => item.baseId === baseId);
  if (!group) return groups;
  return [group, ...groups.filter((item) => item.baseId !== baseId)];
}

/**
 * The base area's verdict for the followed sport (spec 10.7): the Companion home shows it when
 * no free time is set, instead of an empty "When would you like to go?" prompt. Labelled as
 * the area's best conditions, distinct from the personal pick that weighs drive time.
 */
export type CompanionBaseAreaVerdict = Readonly<{
  areaSlug: string;
  areaName: string;
  sport: string;
  sportLabel: string;
  kind: AreaVerdictKind;
  heading: string;
  headline: string;
  experienced: string | null;
  detail: string;
  asOfLabel: string;
  seeAllHref: string;
  seeAllLabel: string;
  publicLink: Readonly<{ href: string; label: string }> | null;
}>;

const SKI_BOARD_SPORTS = new Set(["piste", "off-piste", "touring"]);

function spotNounFor(sport: string): string {
  if (SKI_BOARD_SPORTS.has(sport)) return "resorts";
  if (sport === "sailing") return "venues";
  if (sport === "mtb") return "trail centres";
  return "spots";
}

/** Pure: shapes the area verdict for the home fallback panel. */
export function buildCompanionBaseAreaVerdict(input: Readonly<{
  board: AreaBoard;
  now: Date;
  baseId?: string | null;
  /** Names an ancestor area whose page the link opens. */
  areaNameFor?: (slug: string) => string | null;
}>): CompanionBaseAreaVerdict {
  const { board } = input;
  const verdict = buildAreaVerdict({ board, week: null, season: null, snow: null, now: input.now });
  const label = localSportLabel(board.sport);
  const lowerLabel = board.sport === "sup" ? "SUP" : label.toLowerCase();
  return {
    areaSlug: board.area.slug,
    areaName: board.area.name,
    sport: board.sport,
    sportLabel: label,
    kind: verdict.kind,
    heading: `Best conditions in ${board.area.name}`,
    headline: verdict.headline,
    experienced: verdict.experienced,
    detail: verdict.detail,
    asOfLabel: verdict.asOfLabel,
    seeAllHref: localComparisonHref({ mode: "area", area: board.area.slug, sport: board.sport, base: input.baseId ?? null }),
    seeAllLabel: `See all ${board.area.name} ${lowerLabel} ${spotNounFor(board.sport)} today`,
    publicLink: board.sport === "surf" || board.sport === "piste"
      ? (() => {
          const link = localPublicLink(board.area.slug, board.area.name, board.sport, input.areaNameFor);
          return link && link.href.startsWith(`/conditions/${board.sport}/`) ? link : null;
        })()
      : null,
  };
}
